import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { tg } from '../lib/tg';
import { useAppPath } from '../lib/appBase';
import { resetOnboarded } from '../lib/onboarding';
import { useSoundEnabled } from '../lib/prefs';
import { useTheme } from '../lib/theme';
import {
  ActivityIcon,
  BellIcon,
  BookIcon,
  GiftIcon,
  HomeIcon,
  QuestionIcon,
  SettingsIcon,
  Sheet,
  ThemeToggle,
  WalletIcon,
} from './ui';
import { BrandMark } from './BrandMark';
import { KillSwitchBanner } from './KillSwitchBanner';
import {
  useMe,
  useNotificationPrefs,
  usePatchNotificationPrefs,
} from '../hooks/useApiQuery';
import { useUnreadNotificationsCount } from '../hooks/useNotifications';

export function Layout() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const me = useMe();
  const unread = useUnreadNotificationsCount();
  const nav = useNavigate();
  const ap = useAppPath();
  const homePath = ap('/');

  // 4-item bottom nav — Home / Wallet / Activity / Pod.
  // Deposit and Withdraw are merged into /wallet with an internal tab.
  // Paths run through `ap()` so the same Layout drives both the flat
  // Telegram surface and the `/app`-prefixed browser cabinet.
  const navItems = [
    { to: homePath, label: t('nav.dashboard'), Icon: HomeIcon },
    { to: ap('/wallet'), label: t('nav.wallet'), Icon: WalletIcon },
    { to: ap('/activity'), label: t('nav.activity'), Icon: ActivityIcon },
    { to: ap('/referrals'), label: t('nav.referrals'), Icon: GiftIcon },
  ];

  const firstInitial =
    me.data?.tg_first_name?.[0]?.toUpperCase() ??
    me.data?.tg_username?.[0]?.toUpperCase() ??
    '·';

  const changeLang = (lang: 'ru' | 'en') => {
    void i18n.changeLanguage(lang);
    try {
      localStorage.setItem('polybot.lang', lang);
    } catch {
      /* storage blocked (iOS private mode) — language still changes for
         this session, just isn't remembered on reload */
    }
  };

  return (
    <div className="flex h-full flex-col bg-tg-bg text-tg-text">
      <header className="sticky top-0 z-20 bg-tg-bg/85 px-4 pt-3 pb-2 backdrop-blur-md safe-area-top">
        <div className="mx-auto flex max-w-app items-center justify-between">
          <NavLink
            to={homePath}
            className="pb-focus-ring -ml-1 inline-flex items-center gap-2 rounded-pb-sm px-1 py-0.5"
            aria-label={t('brand.name')}
          >
            <BrandMark size="md" />
          </NavLink>
          <div className="flex items-center gap-1.5">
            <HeaderIconButton
              label={t('menu.how_it_works')}
              onClick={() => {
                tg.hapticLight();
                nav(ap('/how-it-works'));
              }}
            >
              <QuestionIcon size={18} />
            </HeaderIconButton>
            <HeaderIconButton
              label={t('menu.notifications')}
              onClick={() => {
                tg.hapticLight();
                nav(ap('/notifications'));
              }}
              badge={unread.data?.unread_count ?? 0}
            >
              <BellIcon size={18} />
            </HeaderIconButton>
            <HeaderIconButton
              label={t('menu.title')}
              onClick={() => {
                tg.hapticLight();
                setMenuOpen(true);
              }}
            >
              <SettingsIcon size={18} />
            </HeaderIconButton>
            <span
              aria-hidden
              className="pb-display ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-pb-accent-soft text-[15px] text-pb-accent"
            >
              {firstInitial}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-[88px]">
        {/*
         * Kill-switch banner sits inside the scroll container so it
         * sticks to the top of the visible content (under the header)
         * rather than below the address bar on mobile. The negative
         * `mx-5` inside the banner cancels page padding.
         */}
        <div className="mx-auto max-w-app px-5 pt-2">
          <KillSwitchBanner />
        </div>
        <Outlet />
      </main>

      <nav
        className="fixed inset-x-0 bottom-0 z-10 border-t border-pb-border-soft bg-tg-bg/95 backdrop-blur-md safe-area-bottom"
        aria-label={t('nav.bar_label')}
      >
        <div className="mx-auto grid max-w-app grid-cols-4">
          {navItems.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === homePath}
              onClick={() => tg.hapticLight()}
              className={({ isActive }) =>
                clsx(
                  'pb-focus-ring relative flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium',
                  isActive ? 'text-pb-accent' : 'text-tg-hint',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className={clsx(
                      'flex h-7 w-10 items-center justify-center rounded-full transition-all',
                      isActive && 'bg-pb-accent-soft',
                    )}
                  >
                    <Icon size={20} />
                  </span>
                  <span className="tracking-wide">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <SettingsSheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        lang={i18n.language}
        onChangeLang={changeLang}
        onReplayOnboarding={() => {
          setMenuOpen(false);
          resetOnboarded();
          nav(ap('/welcome'));
        }}
      />
    </div>
  );
}

function HeaderIconButton({
  label,
  onClick,
  badge,
  children,
}: {
  label: string;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        badge && badge > 0 ? `${label} (${badge})` : label
      }
      className="pb-focus-ring relative flex h-9 w-9 items-center justify-center rounded-full border border-pb-border-soft text-tg-hint transition-colors hover:bg-pb-paper2 hover:text-tg-text"
    >
      {children}
      {badge && badge > 0 ? (
        <span
          aria-hidden
          className="pb-numeral absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-pb-danger px-1 text-[10px] font-semibold leading-none text-white"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}

function SettingsSheet({
  open,
  onClose,
  lang,
  onChangeLang,
  onReplayOnboarding,
}: {
  open: boolean;
  onClose: () => void;
  lang: string;
  onChangeLang: (l: 'ru' | 'en') => void;
  onReplayOnboarding: () => void;
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [soundEnabled, setSoundEnabled] = useSoundEnabled();
  return (
    <Sheet open={open} onClose={onClose} title={t('menu.title')}>
      <div className="space-y-1">
        <MenuRow
          label={t('menu.theme')}
          hint={
            theme === 'dark' ? t('menu.theme_dark') : t('menu.theme_light')
          }
          trailing={<ThemeToggle variant="segmented" />}
        />
        <MenuRow
          label={t('menu.language')}
          trailing={
            <div className="flex items-center gap-1 rounded-pb-md bg-pb-paper2 p-1">
              {(['ru', 'en'] as const).map((lng) => (
                <button
                  key={lng}
                  type="button"
                  onClick={() => onChangeLang(lng)}
                  className={clsx(
                    'rounded-pb-sm px-2 py-1 text-xs font-medium uppercase',
                    lang.startsWith(lng)
                      ? 'bg-pb-accent-soft text-pb-accent shadow-pb-sm'
                      : 'text-tg-hint',
                  )}
                >
                  {lng}
                </button>
              ))}
            </div>
          }
        />
        <MenuRow
          label={t('menu.sound')}
          hint={t('menu.sound_hint')}
          trailing={
            <ToggleSwitch
              checked={soundEnabled}
              onChange={setSoundEnabled}
              ariaLabel={t('menu.sound')}
            />
          }
        />
        <MenuButtonRow
          icon={<BookIcon size={18} />}
          label={t('menu.replay_onboarding')}
          onClick={onReplayOnboarding}
        />
      </div>

      <NotificationPrefsSection />

      <p className="pb-caption mt-6 text-center">
        {t('brand.name')} ·{' '}
        <a
          href="https://t.me/kitpod_support"
          target="_blank"
          rel="noopener noreferrer"
          className="text-pb-accent hover:underline"
        >
          {t('menu.support')}
        </a>
      </p>
    </Sheet>
  );
}

function MenuRow({
  label,
  hint,
  trailing,
}: {
  label: string;
  hint?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 rounded-pb-md px-3 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-[15px] text-tg-text">{label}</p>
        {hint ? <p className="pb-caption mt-0.5">{hint}</p> : null}
      </div>
      {trailing}
    </div>
  );
}

function MenuButtonRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="pb-focus-ring flex w-full items-center gap-3 rounded-pb-md border border-pb-border-soft bg-pb-paper2 px-3 py-3 text-left hover:bg-pb-accent-soft hover:border-pb-accent-line"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-pb-accent-soft text-pb-accent">
        {icon}
      </span>
      <span className="flex-1 text-[15px] font-medium text-tg-text">
        {label}
      </span>
      <span aria-hidden className="text-pb-accent text-[16px]">
        ›
      </span>
    </button>
  );
}

/**
 * Telegram notifications opt-out section in the Settings sheet.
 * Groups the 9 backend kinds into 5 user-friendly toggles. Each
 * group toggle PATCHes the kinds it controls to the same value, so
 * "off" = mute the whole category.
 *
 * The user_notifications page on the webapp is unaffected — these
 * toggles only gate the *push* surface (TG forwarder).
 */
function NotificationPrefsSection() {
  const { t } = useTranslation();
  const q = useNotificationPrefs();
  const patch = usePatchNotificationPrefs();
  const prefs = q.data?.prefs ?? {};

  // UI-grouping → list of backend kinds. The group reads as ON when
  // ANY of its kinds is enabled (vs. all): trade.opened defaults
  // false but settled/cancelled default true, and the group should
  // still surface as ON for that default-user state. Flipping off
  // mutes the whole category; flipping back on re-enables every
  // kind including the previously off-by-default ones.
  const groups: { key: string; kinds: string[] }[] = [
    {
      key: 'wallet',
      kinds: [
        'deposit.confirmed',
        'withdrawal_decision',
        'fee_charged',
        'referral.earning',
      ],
    },
    { key: 'system', kinds: ['kill_switch'] },
    {
      key: 'trades',
      kinds: ['trade.opened', 'trade.settled', 'trade.cancelled'],
    },
  ];

  const isGroupOn = (kinds: string[]) =>
    kinds.some((k) => prefs[k] !== false);

  const toggleGroup = (kinds: string[], next: boolean) => {
    const partial = Object.fromEntries(kinds.map((k) => [k, next]));
    patch.mutate(partial);
  };

  return (
    <div className="mt-6">
      <p className="pb-eyebrow px-3 mb-2">{t('menu.tg_notifs.heading')}</p>
      <div className="space-y-1">
        {groups.map((g) => {
          const on = isGroupOn(g.kinds);
          return (
            <MenuRow
              key={g.key}
              label={t(`menu.tg_notifs.${g.key}.label`)}
              hint={t(`menu.tg_notifs.${g.key}.hint`)}
              trailing={
                <ToggleSwitch
                  checked={on}
                  onChange={(v) => toggleGroup(g.kinds, v)}
                  ariaLabel={t(`menu.tg_notifs.${g.key}.label`)}
                />
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={clsx(
        'pb-focus-ring relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
        checked ? 'bg-pb-accent' : 'bg-pb-paper2',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow-pb-sm transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
